import Container from '@risk-smart/themed-cloudscape-components/container';
import Icon from '@risk-smart/themed-cloudscape-components/icon';
import clsx from 'clsx';
import dayjs from 'dayjs';
import type { FC } from 'react';

import styles from './style.module.scss';
import type { NotificationItem } from './useNotificationItems';

/** A well-formed friendly id: a short type prefix + sequence number, e.g. `R-187`, `A-170`. */
const FRIENDLY_ID = /^[A-Za-z]{1,5}-\d+$/;

type Props = {
  item: NotificationItem;
  onArchive: () => void;
  onRead: () => void;
};

export const NotificationCell: FC<Props> = ({ item, onArchive, onRead }) => {
  // `type` and `name` arrive already separated — the in-app template emits them as two
  // paragraphs and the renderer reads them positionally (no string parsing here). The
  // friendly id (e.g. `R-187`) is on `item.id` for both paths; `FRIENDLY_ID` rejects a
  // malformed legacy id ("A-undefined" when the entity wasn't found).
  const { type, name } = item;
  const friendlyId = item.id && FRIENDLY_ID.test(item.id) ? item.id : null;
  const typeLine = [friendlyId && `(${friendlyId})`, type]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.cell}>
      <Container variant={'stacked'}>
        <div className={'flex flex-row'}>
          <div className={'grow cursor-pointer'} onClick={onRead}>
            <div className={'flex flex-row items-center gap-2'}>
              <div
                // top-[2px]: optical nudge so the dot sits on the type text's
                // baseline rather than its cap line (visual only, no layout shift).
                className={clsx(
                  'flex-none rounded-full h-2 w-2 relative top-[2px]',
                  {
                    'bg-teal': !item.feedItem.read_at && item.url !== null,
                    'bg-grey': !!item.feedItem.read_at || item.url === null,
                  }
                )}
              ></div>
              {typeLine && (
                <div
                  className={'text-grey500 text-xs font-medium leading-[1.5]'}
                >
                  {typeLine}
                </div>
              )}
            </div>

            {name && (
              <div className={'text-sm font-semibold leading-[1.5] mt-[6px]'}>
                {name}
              </div>
            )}

            <div className={'text-grey500 text-xs leading-[1.5] mt-[6px]'}>
              {dayjs(item.feedItem.inserted_at).format('HH:mm, D MMM YYYY')}
            </div>
          </div>

          <span
            className={'cursor-pointer hover:opacity-50 text-grey500 -mt-1'}
            onClick={(e) => {
              onArchive();
              e.preventDefault();
            }}
          >
            <Icon name={'close'} size={'small'} />
          </span>
        </div>
      </Container>
    </div>
  );
};
